import { JSX, useContext, useEffect, useState } from "react";
import { Client, ClientType } from "@app/client/client";
import { ClientService } from "@app/client/clientService";
import {
  NetworkTaxonomyData,
  NetworkTaxonomyFileType,
  NetworkTaxonomyIdentifier,
  NetworkTaxonomyLineOfBusinessIdentifier,
  NetworkTaxonomyLineOfBusinessSimple,
  NetworkTaxonomyLineOfBusinessType,
  ProjectUtilizationCategory
} from "@app/network-taxonomies/networkTaxonomy";
import NetworkTaxonomyService from "@app/network-taxonomies/networkTaxonomyService";
import { BulkProjectEditChangeReason } from "@app/projects/edit/bulkProjectEditChangeReason";
import InfoIndicator from "@app/shared/InfoIndicator";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { QESTextArea } from "@app/shared/qes-standards/forms/TextArea";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import { isNullOrUndefined } from "@app/shared/utility/isNullOrUndefined";
import { UserContext } from "@app/user/UserContext";
import Roles from "@app/user/roles";

interface NetworkTaxonomyContentPanelProps {
  networkTaxonomyData: NetworkTaxonomyData;
  onNetworkTaxonomyChange: (networkTaxonomyData: NetworkTaxonomyData, isValid: boolean, invalidProperties?: BulkProjectEditChangeReason[]) => void;
  disabled: boolean;
  isBulkEdit?: boolean;
}

function NetworkTaxonomyContentPanel(props: NetworkTaxonomyContentPanelProps): JSX.Element {
  const reasonMaxLength = 100;

  const networkTaxonomyService = new NetworkTaxonomyService();

  const copyTaxonomomy = (taxonomy: NetworkTaxonomyData, copyToEditPage: boolean) => {
    const networkTaxonomy = !!taxonomy ? { ...taxonomy } : {};

    networkTaxonomy.identifiers = [];
    taxonomy?.identifiers?.forEach(id => networkTaxonomy.identifiers.push({ ...id }));

    if (!props.isBulkEdit)
      if (copyToEditPage)
        networkTaxonomy.fileTypeId = !!taxonomy ? networkTaxonomy.fileTypeId ?? -2 : null
      else
        if (networkTaxonomy.fileTypeId === -2)
          networkTaxonomy.fileTypeId = null

    return networkTaxonomy;
  }

  const [networkTaxonomy, setNetworkTaxonomy] = useState<NetworkTaxonomyData>(copyTaxonomomy(props.networkTaxonomyData, true));

  const [linesOfBusiness, setLinesOfBusiness] = useState<NetworkTaxonomyLineOfBusinessSimple[]>([]);
  const [linesOfBusinessTypes, setLinesOfBusinessTypes] = useState<NetworkTaxonomyLineOfBusinessType[]>([]);
  const [linesOfBusinessIdentifiers, setLinesOfBusinessIdentifiers] = useState<NetworkTaxonomyLineOfBusinessIdentifier[]>([]);
  const [fileTypes, setFileTypes] = useState<NetworkTaxonomyFileType[]>([]);
  const [projectUtilizationCategories, setProjectUtilizationCategories] = useState<ProjectUtilizationCategory[]>([]);

  const [regulatorAssignmentClients, setRegulatorAssignmentClients] = useState<Client[]>([]);

  const [selectedLineOfBusinessDisabled, setSelectedLineOfBusinessDisabled] = useState(false);

  const userContext = useContext(UserContext);
  const clientService = new ClientService();

  useEffect(() => {
    networkTaxonomyService.getLinesOfBusiness()
      .then(linesOfBusiness => {
        setLinesOfBusiness(linesOfBusiness);
      });

    networkTaxonomyService.getFileTypes()
      .then(fileTypes => {
        setFileTypes(fileTypes);
      })

    networkTaxonomyService.getProjectUtilizationCategories()
      .then(projectUtilizationCategories => {
        setProjectUtilizationCategories(projectUtilizationCategories);
      });

    clientService.getAllClients()
      .then(clients => {
        setRegulatorAssignmentClients(clients.filter(client => client.clientType === ClientType.TwoSidedRegulator));
      });
  }, [])

  useEffect(() => {
    if (!props.networkTaxonomyData || !props.isBulkEdit)
      return;

    setNetworkTaxonomy(copyTaxonomomy(props.networkTaxonomyData, true));
  }, [props.networkTaxonomyData])

  useEffect(() => {
    if (!networkTaxonomy?.lineOfBusinessId)
      return;

    if (networkTaxonomy.lineOfBusinessId === -1) {
      setLinesOfBusinessTypes([]);
      setLinesOfBusinessIdentifiers([]);
      setSelectedLineOfBusinessDisabled(false);
      return;
    }

    networkTaxonomyService.getLineOfBusinessDetails(networkTaxonomy.lineOfBusinessId)
      .then(lob => {
        setLinesOfBusinessTypes(lob.lineOfBusinessTypes);

        setLinesOfBusinessIdentifiers(lob.identifiers);

        setSelectedLineOfBusinessDisabled(lob.disabledLineOfBusiness);
      });
  }, [networkTaxonomy.lineOfBusinessId])

  useEffect(() => {
    if (props.disabled)
      return;

    let isValid = true;
    const invalidProperties = [];
    if (props.isBulkEdit) {
      // Add dependency checks here as they are added
      if (networkTaxonomy.fileTypeId === -2 && !IsReasonValid(networkTaxonomy.fileTypeReason)) {
        isValid = false;
        invalidProperties.push(BulkProjectEditChangeReason.FileTypeReason);
      }

      if (networkTaxonomy.lineOfBusinessId > 0) {
        if (selectedLineOfBusinessDisabled) {
          if (!IsReasonValid(networkTaxonomy.reason)) {
            isValid = false;
            invalidProperties.push(BulkProjectEditChangeReason.Reason)
          }
        } else {
          if (linesOfBusinessTypes.length !== 0 && !networkTaxonomy.lineOfBusinessTypeId) {
            isValid = false;
            invalidProperties.push(BulkProjectEditChangeReason.LineOfBusinessTypeId)
          }
        }
      }
    }
    else {
      if (!networkTaxonomy.lineOfBusinessId) {
        isValid = false;
      }

      if (!networkTaxonomy.fileTypeId || (networkTaxonomy.fileTypeId === -2 && !IsReasonValid(networkTaxonomy.fileTypeReason)))
        isValid = false;

      if (!networkTaxonomy.projectUtilizationCategoryId)
        isValid = false;

      if (selectedLineOfBusinessDisabled) {
        if (!IsReasonValid(networkTaxonomy.reason))
          isValid = false;
      } else {
        if (linesOfBusinessTypes.length !== 0 && isNullOrUndefined(networkTaxonomy.lineOfBusinessTypeId))
          isValid = false;

        networkTaxonomy.identifiers?.forEach(id => {
          const validation = GetMatchingLineOfBusinessIdentifier(id?.lineOfBusinessIdentifierId)?.typeScriptRegexValidation;

          isValid &&= ValidateIdentifier(id, validation);
        });
      }
    }

    const networkTaxonomyClone = copyTaxonomomy(networkTaxonomy, false);

    props.onNetworkTaxonomyChange(networkTaxonomyClone, isValid, invalidProperties);
  }, [networkTaxonomy, linesOfBusiness, linesOfBusinessTypes]
  )

  const selectedFileTypeIdChange = (fileTypeId: number, value: string) => {
    let suggestedProjectUtilizationCategoryId = networkTaxonomy.projectUtilizationCategoryId;
    const selectedFileType = fileTypes.find(x => x.fileTypeId == fileTypeId);
    if (!networkTaxonomy.projectUtilizationCategoryId && !!selectedFileType?.suggestedProjectUtilizationCategoryId)
      suggestedProjectUtilizationCategoryId = selectedFileType.suggestedProjectUtilizationCategoryId;

    setNetworkTaxonomy(networkTaxonomy => ({
      ...networkTaxonomy,
      fileType: !!fileTypeId ? value : "",
      fileTypeId: fileTypeId,
      fileTypeReason: null,
      projectUtilizationCategoryId: suggestedProjectUtilizationCategoryId
    }))
  }

  const selectedProjectUtilizationCategoryIdChange = (projectUtilizationCategoryId: number, value: string) => {
    setNetworkTaxonomy(networkTaxonomy => ({
      ...networkTaxonomy,
      projectUtilizationCategory: !!projectUtilizationCategoryId ? value: "",
      projectUtilizationCategoryId: projectUtilizationCategoryId
    }));
  }

  const selectedLineOfBusinessIdChange = (lineOfBusinessId: number, value: string) => {
    setNetworkTaxonomy(networkTaxonomy => ({
      ...networkTaxonomy,
      identifiers: [],
      lineOfBusiness: !!lineOfBusinessId ? value : "",
      lineOfBusinessId,
      lineOfBusinessType: "",
      lineOfBusinessTypeId: null,
      reason: null,
    }));
  }

  const selectedLineOfBusinessTypeIdChange = (lineOfBusinessTypeId: number, value: string) => {
    let identifiers: NetworkTaxonomyIdentifier[] = networkTaxonomy.identifiers;

    linesOfBusinessIdentifiers.forEach(lobId => {
      if (lineOfBusinessTypeId && lobId.lineOfBusinessTypeId && lobId.lineOfBusinessTypeId !== lineOfBusinessTypeId)
        identifiers = identifiers.filter(id => id.lineOfBusinessIdentifierId !== lobId.lineOfBusinessIdentifierId);

      else if (!lineOfBusinessTypeId && lobId.lineOfBusinessTypeId && lobId.lineOfBusinessTypeId > 0)
        identifiers = identifiers.filter(id => id.lineOfBusinessIdentifierId !== lobId.lineOfBusinessIdentifierId);
    });
    setNetworkTaxonomy(networkTaxonomy => ({
      ...networkTaxonomy,
      identifiers: identifiers.sort((a, b) => a.order - b.order),
      lineOfBusinessType: !!lineOfBusinessTypeId ? value : "",
      lineOfBusinessTypeId,
    }))
  }

  const selectedRegulatorAssignmentClientIdChange = (regulatorAssignmentClientId: number, value: string) => {
    setNetworkTaxonomy(networkTaxonomy => ({
      ...networkTaxonomy,
      regulatorAssignmentClient: !!regulatorAssignmentClientId ? value: "",
      regulatorAssignmentClientId,
    }));
  }

  const AddIdentifier = () => {
    const identifiers: NetworkTaxonomyIdentifier[] = networkTaxonomy.identifiers;
    const maxOrder = networkTaxonomy.identifiers.length === 0 ? 1 : networkTaxonomy.identifiers.sort((a, b) => a.order - b.order)[networkTaxonomy.identifiers.length - 1].order + 1;

    const newIdentifier: NetworkTaxonomyIdentifier = {
      lineOfBusinessIdentifierId: null,
      name: "",
      networkTaxonomyId: networkTaxonomy?.networkTaxonomyId ?? 0,
      networkTaxonomyIdentifierId: null,
      order: maxOrder,
      value: ""
    };
    identifiers.push(newIdentifier);

    setNetworkTaxonomy(networkTaxonomy => ({
      ...networkTaxonomy,
      identifiers
    }));
  }

  const RemoveIdentifier = (index: number) => {
    const identifiers: NetworkTaxonomyIdentifier[] = networkTaxonomy.identifiers;
    identifiers.splice(index, 1);

    let order = 1;
    networkTaxonomy.identifiers.forEach(id => {
      id.order = order;
      order++;
    });

    setNetworkTaxonomy(networkTaxonomy => ({
      ...networkTaxonomy,
      identifiers
    }));
  }

  const UpdateIdentifierName = (index: number, identifierName: string) => {
    const identifiers: NetworkTaxonomyIdentifier[] = networkTaxonomy.identifiers;

    identifiers[index].name = identifierName;

    setNetworkTaxonomy(networkTaxonomy => ({
      ...networkTaxonomy,
      identifiers
    }));
  }

  const UpdateIdentifierLineOfBusinessIdentifierId = (index: number, lineOfBusinessIdentifierId: number, identifierName: string) => {
    const identifiers: NetworkTaxonomyIdentifier[] = networkTaxonomy.identifiers;
    identifiers[index].lineOfBusinessIdentifierId = lineOfBusinessIdentifierId;
    identifiers[index].name = !!lineOfBusinessIdentifierId ? identifierName : "";


    setNetworkTaxonomy(networkTaxonomy => ({
      ...networkTaxonomy,
      identifiers
    }));
  }

  const UpdateIdentifierValue = (index: number, identifierValue: string) => {
    const identifiers: NetworkTaxonomyIdentifier[] = networkTaxonomy.identifiers;
    identifiers[index].value = identifierValue;

    setNetworkTaxonomy(networkTaxonomy => ({
      ...networkTaxonomy,
      identifiers
    }));
  }

  const IsReasonValid = (reason: string) => {
    return !!reason && reason.length > 0 && reason.length <= reasonMaxLength;
  }

  const GetMatchingLineOfBusinessIdentifier = (lineOfBusinessIdentifierId: number) => {
    const lobIdentifier = linesOfBusinessIdentifiers.find(lobId => lobId.lineOfBusinessIdentifierId === lineOfBusinessIdentifierId);
    if (isNullOrUndefined(lobIdentifier))
      return null;

    return lobIdentifier;
  }

  const ValidateLineOfBusinessType = () => {
    return (networkTaxonomy.lineOfBusinessTypeId !== null || linesOfBusinessTypes.length === 0);
  }

  const ValidateIdentifier = (identifier: NetworkTaxonomyIdentifier, regexExpress: null | string) => {
    if (!identifier.lineOfBusinessIdentifierId && !identifier.name)
      return false;

    return ValidateIdentifierValue(identifier, regexExpress);
  }

  const ValidateIdentifierValue = (identifier: NetworkTaxonomyIdentifier, regexExpress: null | string) => {
    if (!identifier.value)
      return false;

    if (isNullOrUndefined(regexExpress))
      return true;

    const matches = identifier.value.match(regexExpress);
    if (matches === undefined || matches === null || matches.length !== 1 || matches[0] !== identifier.value)
      return false;

    return true;
  }

  return (
    <>
        <div>
          <QESSelect
            testId="network-taxonomy-content-panel__network_taxonomy_file_type"
            containerClassName="col-6 mt-2 px-2"
            label="File Type"
            allowEmpty={true}
            placeholder="Select File Type"
            value={networkTaxonomy.fileTypeId ?? ""}
            onChange={(e) => selectedFileTypeIdChange(e.target.value ? parseInt(e.target.value) : null, e.target.selectedOptions[0].text)}
            isValid={!!networkTaxonomy.fileTypeId || props.isBulkEdit}
            required={!props.isBulkEdit}
            disabled={props.disabled}
          >
            {networkTaxonomy.fileTypeId === -1 && <option key="-1" value="-1">Different File Type Being Used</option>}
            {!!fileTypes && fileTypes.map(f => (
              <option key={f.fileTypeId} value={f.fileTypeId}>{f.name}</option>
            ))}
            <option key="-2" value="-2" >Other Data</option>
          </QESSelect>
          {(userContext.roles.includes(Roles.QUEST_ADMIN)) &&
            <QESSelect
              testId="network-taxonomy-content-panel__network_taxonomy_regulator_assignment"
              containerClassName="col-6 mt-2 px-2"
              label="Regulator Assignment"
              disabled={props.disabled}
              placeholder={"Select Regulator Assignment"}
              allowEmpty={true}
              value={
                !!networkTaxonomy.regulatorAssignmentClientId ?
                networkTaxonomy.regulatorAssignmentClientId.toString() :
                ""
              }
              onChange={(e) => {
                selectedRegulatorAssignmentClientIdChange(e.target.value ? parseInt(e.target.value) : null, e.target.selectedOptions[0].text);
              }}
            >
              {networkTaxonomy.regulatorAssignmentClientId === -1 && <option key="-1" value="-1">Different Regulator Assignments Being Used</option>}
              {
                regulatorAssignmentClients
                  .map(client => <option key={client.clientId} value={client.clientId}>{client.name}</option>)
              }
            </QESSelect>
          }
          {(networkTaxonomy.fileTypeId === -2) &&
            <QESTextInput
              testId="network-taxonomy-content-panel__network_taxonomy_file_type_reason"
              containerClassName="col-6 mt-2 px-2"
              label="Other File Type"
              value={networkTaxonomy.fileTypeReason === "-1" ? "Different Other File Type Being Used" : networkTaxonomy.fileTypeReason ?? ""}
              onChange={(e) => setNetworkTaxonomy(networkTaxonomy => ({ ...networkTaxonomy, fileTypeReason: (e.target.value) }))}
              maxLength={reasonMaxLength}
              required={true}
              isValid={IsReasonValid(networkTaxonomy.fileTypeReason)}
            />
          }
          <QESSelect
            testId="network-taxonomy-content-panel__network_taxonomy_project_utilization_category"
            containerClassName="col-6 mt-2 px-2"
            label={
              <>
              Project Utilization Category
              <span>&nbsp;</span>
                <InfoIndicator className={"informational-indicator"}
                  tooltipTitle={
                    <div style={{ textAlign: "left" }}>
                      <ul style={{ margin: 0, paddingInlineStart: 20 }} >
                        <li><strong>Fully Contracted Network</strong>: A project that best represents a client&apos;s network across their full active service area. The project can also include opportunity, pending, approved counties and/or LOI flagged providers. These counties and LOI flagged providers will not be leveraged in analysis.</li>
                        <li><strong>Scenario/Test</strong>: A project that is being used for testing or scenario modeling purposes by a client. Examples: expansion only, partial service areas, user specific test tiles, network modeling with removals or additions.</li>
                        <li><strong>Accuracy</strong>: A project that only contains accuracy specific data not tied to an adequacy analysis.</li>
                      </ul>
                    </div>
                  }
                  tooltipPlacement={"right"}
                  tooltipWidth={"20rem"} />
            </>
            }
            allowEmpty={true}
            placeholder="Select Project Utilization Category"
            value={networkTaxonomy.projectUtilizationCategoryId ?? ""}
            onChange={(e) => selectedProjectUtilizationCategoryIdChange(e.target.value ? parseInt(e.target.value) : null, e.target.selectedOptions[0].text)}
            isValid={!!networkTaxonomy.projectUtilizationCategoryId || props.isBulkEdit}
            required={!props.isBulkEdit}
            disabled={props.disabled}
          >
            {networkTaxonomy.projectUtilizationCategoryId === -1 && <option key="-1" value="-1">Different Project Utilization Category Being Used</option>}
            {!!projectUtilizationCategories && projectUtilizationCategories.map(p => (
              <option key={p.projectUtilizationCategoryId} value={p.projectUtilizationCategoryId}>{p.name}</option>
            ))}
          </QESSelect>
          <QESSelect
            testId="network-taxonomy-content-panel__network_taxonomy_line_of_business"
            containerClassName="col-6 mt-2 px-2"
            label="Line Of Business"
            allowEmpty={true}
            placeholder="Select Line of Business"
            value={networkTaxonomy.lineOfBusinessId ?? ""}
            onChange={(e) => selectedLineOfBusinessIdChange(e.target.value ? parseInt(e.target.value) : null, e.target.selectedOptions[0].text)}
            isValid={!!networkTaxonomy.lineOfBusinessId || props.isBulkEdit}
            required={!props.isBulkEdit}
            disabled={props.disabled}
          >
            {networkTaxonomy.lineOfBusinessId === -1 && <option key="-1" value="-1">Different Line Of Business Being Used</option>}
            {!!linesOfBusiness && linesOfBusiness.map(lob => (
              <option key={lob.lineOfBusinessId} value={lob.lineOfBusinessId}>{lob.name}</option>
            ))}
          </QESSelect>
          {!!networkTaxonomy.lineOfBusinessId && selectedLineOfBusinessDisabled &&
            <QESTextArea
              testId={`network-taxonomy-content-panel__network_taxonomy_reason`}
              containerClassName="col-6 mt-2 px-2"
              label="Reason"
              maxLength={reasonMaxLength}
              value={networkTaxonomy.reason === "-1" ? "Different Reason Being Used" : networkTaxonomy.reason ?? ""}
              onChange={(e) => setNetworkTaxonomy(networkTaxonomy => ({ ...networkTaxonomy, reason: (e.target.value) }))}
              isValid={IsReasonValid(networkTaxonomy.reason)}
              required={true}
            />
          }
          {!!networkTaxonomy.lineOfBusinessId && !selectedLineOfBusinessDisabled &&
            <>
              {linesOfBusinessTypes && linesOfBusinessTypes.length > 0 &&
                <QESSelect
                  testId="network-taxonomy-content-panel__network_taxonomy_line_of_business_type"
                  containerClassName="col-6 mt-2 px-2"
                  label="Line Of Business Type"
                  allowEmpty={true}
                  placeholder="Select Line of Business Type"
                  value={networkTaxonomy.lineOfBusinessTypeId ?? ""}
                  onChange={(e) => selectedLineOfBusinessTypeIdChange(e.target.value ? parseInt(e.target.value) : null, e.target.selectedOptions[0].text)}
                  isValid={ValidateLineOfBusinessType()}
                  required={linesOfBusinessTypes?.length > 0}
                  disabled={props.disabled}
              >
                  {networkTaxonomy.lineOfBusinessTypeId === -1 && <option key="-1" value="-1">Different Line Of Business Type Being Used</option>}
                  {!!linesOfBusinessTypes && linesOfBusinessTypes.map(type => (
                    <option key={type.lineOfBusinessTypeId} value={type.lineOfBusinessTypeId}>{type.name}</option>
                  ))}
                  required={false}
                </QESSelect>
              }
              {!props.isBulkEdit &&
                <>
                  <div className="row col-6 mt-2 ps-2">
                    <QESButton
                      qesTestId="network-taxonomy-content-panel__add_network_taxonomy_identifier"
                      qesType="secondary"
                      disabled={props.disabled}
                      onClick={AddIdentifier}
                      className="col-auto"
                    >
                      + Add {networkTaxonomy.identifiers.length > 0 && 'Another'} Identifier
                    </QESButton>
                    <label className="mt-2 col-auto" >{networkTaxonomy.identifiers.length} Identifier{networkTaxonomy.identifiers.length > 1 && 's'}</label>
                  </div>

                  {!!networkTaxonomy.identifiers && networkTaxonomy.identifiers
                    .map((identifier, index) => (
                      <div className="row m-2" key={index}>
                        <QESSelect
                          testId={`network-taxonomy-content-panel__network_taxonomy_identifier_type_${index}`}
                          containerClassName="col-3 ps-0"
                          label="Identifier Type"
                          allowEmpty={false}
                          value={identifier.lineOfBusinessIdentifierId ?? "0"}
                          onChange={(e) => UpdateIdentifierLineOfBusinessIdentifierId(index, e.target.value ? parseInt(e.target.value) : null, e.target.selectedOptions[0].text)}
                          disabled={props.disabled}
                        >
                          {!!linesOfBusinessIdentifiers && linesOfBusinessIdentifiers.filter(lobId => !lobId.lineOfBusinessTypeId || lobId.lineOfBusinessTypeId === networkTaxonomy.lineOfBusinessTypeId).map(type => (
                            <option key={type.lineOfBusinessIdentifierId} value={type.lineOfBusinessIdentifierId}>{type.name}</option>
                          ))}
                          <option key="0" value="0" >Other</option>
                        </QESSelect>

                        {!identifier.lineOfBusinessIdentifierId &&
                          <QESTextInput
                            testId={`network-taxonomy-content-panel__network_taxonomy_identifier_name_${index}`}
                            containerClassName="col-3"
                            label="Identifier Name"
                            value={identifier.name}
                            onChange={(e) => UpdateIdentifierName(index, e.target.value)}
                            isValid={!!identifier.name}
                            required={true}
                            disabled={props.disabled}
                          />
                        }

                        <QESTextInput
                          testId={`network-taxonomy-content-panel__network_taxonomy_identifier_name_value_${index}`}
                          containerClassName="col-3"
                          label="Identifier Value"
                          value={identifier.value}
                          onChange={(e) => UpdateIdentifierValue(index, e.target.value)}
                          descriptiveText={GetMatchingLineOfBusinessIdentifier(identifier?.lineOfBusinessIdentifierId)?.uiValidation}
                          isValid={ValidateIdentifierValue(identifier, GetMatchingLineOfBusinessIdentifier(identifier?.lineOfBusinessIdentifierId)?.typeScriptRegexValidation)}
                          required={true}
                          disabled={props.disabled}
                        />

                        <QESButton
                          qesTestId={`network-taxonomy-content-panel__remove_network_taxonomy_identifier_${index}`}
                          qesType="secondary-danger"
                          className={`${!GetMatchingLineOfBusinessIdentifier(identifier?.lineOfBusinessIdentifierId)?.uiValidation ? "transparent-button-del mt-4" : "transparent-button-del"} col-auto`}
                          onClick={() => RemoveIdentifier(index)}
                          disabled={props.disabled}
                          aria-label="Remove">
                            <QesIcon iconName={QesIcons.TrashV1} isDecorative className="align-text-top" />
                        </QESButton>
                      </div>
                    ))
                  }
                </>
              }
            </>
          }
        </div>
    </>
  )
}

export default NetworkTaxonomyContentPanel;